import dayjs from "lib/dayjs";

/** ──────────────────────────────────────────────
 *  Generate normalized weights for distributions
 *  ────────────────────────────────────────────── */
function generateWeights(n, distribution) {
  if (distribution === "normal") {
    const mid = (n - 1) / 2;
    const sigma = n / 6;
    const weights = Array.from({ length: n }, (_, i) =>
      Math.exp(-0.5 * Math.pow((i - mid) / sigma, 2))
    );
    const total = weights.reduce((a, b) => a + b, 0);
    return weights.map((w) => w / total);
  }

  // level distribution
  return Array(n).fill(1 / n);
}

/** ──────────────────────────────────────────────
 *  Generate basic cashflow schedule for FundPlanner
 *  NO maturityDate
 *  NO leadDays
 *  Pure drawDate + amount pattern
 *  ────────────────────────────────────────────── */
export function generateCashflows({
  mode: _mode = "gross",
  startDate,
  frequency = "monthly",
  numPeriods = 12,
  totalAmount = 0,
  distribution = "level",
}) {
  const start = dayjs(startDate);
  if (!start.isValid() || numPeriods <= 0 || totalAmount <= 0) return [];

  // step size
  let stepMonths = 1;
  switch (frequency) {
    case "quarterly":
      stepMonths = 3;
      break;
    case "semiannual":
      stepMonths = 6;
      break;
    case "annual":
      stepMonths = 12;
      break;
  }

  // Draw dates: exactly numPeriods
  const drawDates = Array.from({ length: numPeriods }, (_, i) =>
    start.add(i * stepMonths, "month").format("YYYY-MM-DD")
  );

  // Compute amounts
  const weights = generateWeights(numPeriods, distribution);
  const round2 = (n) => Math.round(n * 100) / 100;

  return drawDates.map((drawDate, i) => {
    const amt = round2(totalAmount * weights[i]);
    return {
      id: i + 1,
      drawDate,
      amount: amt,
    };
  });
}

/** ──────────────────────────────────────────────
 *  SLGS cashflow builder (unchanged)
 *  (still needed by gross/net solvers)
 *  ────────────────────────────────────────────── */
export function buildSLGSCashflow(fundingDate, maturityDate, rate) {
  const start = dayjs(fundingDate);
  const end = dayjs(maturityDate);
  const principal = 1;
  const flows = [];

  if (!start.isValid() || !end.isValid() || !rate) return [];

  const totalDays = end.diff(start, "day");
  const years = totalDays / 360;

  // Short-term SLGS (< 1 year)
  if (years < 1) {
    const interest = principal * rate * (totalDays / 360);
    flows.push({
      date: end.format("YYYY-MM-DD"),
      amount: principal + interest,
    });
    return flows;
  }

  // Coupon-bearing SLGS (≥ 1 year)
  const maturityDay = Math.min(end.date(), 30);
  let couponDate = end.clone();
  const couponDates = [];

  while (couponDate.isAfter(start, "day")) {
    couponDates.unshift(couponDate.clone());
    couponDate = couponDate.subtract(6, "month").date(maturityDay);
  }

  let prevDate = start;

  for (const cd of couponDates) {
    const accrualDays = cd.diff(prevDate, "day");
    const accrualFrac = accrualDays / 360;
    const interest = principal * rate * accrualFrac;
    flows.push({
      date: cd.format("YYYY-MM-DD"),
      amount: interest,
    });
    prevDate = cd;
  }

  // Add principal to final
  if (flows.length) {
    flows[flows.length - 1].amount += principal;
  } else {
    flows.push({ date: end.format("YYYY-MM-DD"), amount: principal });
  }

  return flows;
}
